'use client';

import { useMutation, useQuery } from 'convex/react';
import { api } from 'convex/_generated/api';
import type { FunctionReturnType } from 'convex/server';
import { text as adminText } from '@modules/admin/text_business_logic';
import { ComponentType, useState } from 'react';

export type AdminPlant = FunctionReturnType<typeof api.seed.seedPlants.getAllPlantsAdmin>[number];

export interface AdminProps {
  isLoading: boolean;
  isAdmin: boolean;
  allPlants: AdminPlant[] | undefined;
  showClearConfirm: boolean;
  isClearSeeding: boolean;
  isSeedingGarden: boolean;
  isSeedingSummerGarden: boolean;
  isSeedingPreviewGarden: boolean;
  onOpenClearConfirm: () => void;
  onClearConfirmOpenChange: (open: boolean) => void;
  onClearAndSeedPlants: () => void;
  onSeedTemplateGarden: () => void;
  onSeedSummerTemplateGarden: () => void;
  onSeedPlantImagePreviewGarden: () => void;
}

interface AdminContainerProps {
  View: ComponentType<AdminProps>;
  onNotifyLoading: (message: string) => void;
  onNotifySuccess: (message: string) => void;
  onNotifyError: (message: string) => void;
}

export function AdminContainer({
  View,
  onNotifyLoading,
  onNotifySuccess,
  onNotifyError,
}: AdminContainerProps) {
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearSeeding, setIsClearSeeding] = useState(false);
  const [isSeedingGarden, setIsSeedingGarden] = useState(false);
  const [isSeedingSummerGarden, setIsSeedingSummerGarden] = useState(false);
  const [isSeedingPreviewGarden, setIsSeedingPreviewGarden] = useState(false);

  const isAdmin = useQuery(api.userRoles.queries.isCurrentUserAdmin, {});
  const allPlants = useQuery(api.seed.seedPlants.getAllPlantsAdmin, isAdmin ? {} : 'skip');

  const clearAndSeedPlants = useMutation(api.seed.seedPlants.clearAndSeedPlants);
  const seedTemplateGarden = useMutation(api.seed.seedGardens.seedTemplateGarden);
  const seedSummerTemplateGarden = useMutation(api.seed.seedGardens.seedSummerTemplateGarden);
  const seedPlantImagePreviewGarden = useMutation(api.seed.seedGardens.seedPlantImagePreviewGarden);

  const handleClearAndSeedPlants = async () => {
    try {
      setIsClearSeeding(true);
      onNotifyLoading(adminText.clearingAndSeedingPlants);

      const result = await clearAndSeedPlants({});

      onNotifySuccess(
        adminText.clearAndSeedSuccess(result.insertedSpeciesCount, result.insertedVarietyCount)
      );
    } catch (error: unknown) {
      onNotifyError(adminText.errorMessage(error));
    } finally {
      setIsClearSeeding(false);
      setShowClearConfirm(false);
    }
  };

  const handleSeedGarden = async () => {
    try {
      setIsSeedingGarden(true);
      onNotifyLoading(adminText.creatingTemplateGarden);

      const result = await seedTemplateGarden({});

      onNotifySuccess(adminText.seedGardenSuccess(result.name));
    } catch (error: unknown) {
      onNotifyError(adminText.errorMessage(error));
    } finally {
      setIsSeedingGarden(false);
    }
  };

  const handleSeedSummerGarden = async () => {
    try {
      setIsSeedingSummerGarden(true);
      onNotifyLoading(adminText.creatingTemplateGarden);

      const result = await seedSummerTemplateGarden({});

      onNotifySuccess(adminText.seedGardenSuccess(result.name));
    } catch (error: unknown) {
      onNotifyError(adminText.errorMessage(error));
    } finally {
      setIsSeedingSummerGarden(false);
    }
  };

  const handleSeedPlantImagePreviewGarden = async () => {
    try {
      setIsSeedingPreviewGarden(true);
      onNotifyLoading(adminText.creatingPreviewGarden);

      const result = await seedPlantImagePreviewGarden({});

      onNotifySuccess(
        adminText.previewGardenSuccess(result.name, result.plantCount, result.missingPlantCount)
      );
    } catch (error: unknown) {
      onNotifyError(adminText.errorMessage(error));
    } finally {
      setIsSeedingPreviewGarden(false);
    }
  };

  return (
    <View
      isLoading={isAdmin === undefined}
      isAdmin={isAdmin === true}
      allPlants={allPlants}
      showClearConfirm={showClearConfirm}
      isClearSeeding={isClearSeeding}
      isSeedingGarden={isSeedingGarden}
      isSeedingSummerGarden={isSeedingSummerGarden}
      isSeedingPreviewGarden={isSeedingPreviewGarden}
      onOpenClearConfirm={() => setShowClearConfirm(true)}
      onClearConfirmOpenChange={setShowClearConfirm}
      onClearAndSeedPlants={handleClearAndSeedPlants}
      onSeedTemplateGarden={handleSeedGarden}
      onSeedSummerTemplateGarden={handleSeedSummerGarden}
      onSeedPlantImagePreviewGarden={handleSeedPlantImagePreviewGarden}
    />
  );
}
